import React from 'react';
import clsx from 'clsx';

const PageHeader = ({
  title,
  eyebrow,
  description,
  subtitle,
  breadcrumbs,
  status,
  metadata,
  primaryAction,
  secondaryActions,
  tabs,
  backHref,
  onBack,
  backAction,
  compact = false,
  children,
  className,
  variant = 'default',
  ...props
}) => {
  const variants = {
    default: 'bg-white border-b border-gray-200',
    transparent: 'bg-transparent',
    gray: 'bg-gray-50 border-b border-gray-200'
  };

  const headerClasses = clsx(
    'px-4 sm:px-6 lg:px-8',
    compact ? 'py-4' : 'py-6',
    variants[variant],
    className
  );

  const resolvedDescription = description ?? subtitle;
  const hasStructuredContent = Boolean(
    title
    || eyebrow
    || resolvedDescription
    || (Array.isArray(breadcrumbs) && breadcrumbs.length > 0)
    || status
    || metadata
    || primaryAction
    || secondaryActions
    || tabs
    || backHref
    || onBack
    || backAction
  );

  const resolvedBackAction = backAction || (
    backHref || onBack
      ? (
        <button
          type="button"
          className="inline-flex items-center rounded-md px-2 py-1 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
          onClick={(event) => {
            if (onBack) {
              onBack(event);
              return;
            }
            if (backHref) {
              window.location.assign(backHref);
            }
          }}
        >
          <span aria-hidden="true" className="mr-1">&larr;</span>
          Back
        </button>
      )
      : null
  );

  return (
    <div className={headerClasses} {...props} data-otg-component={props['data-otg-component'] || 'PageHeader'}>
      {hasStructuredContent ? (
        <>
          {Array.isArray(breadcrumbs) && breadcrumbs.length > 0 ? (
            <PageHeaderBreadcrumbs items={breadcrumbs} className={compact ? 'mb-2' : 'mb-4'} />
          ) : null}

          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0 flex-1 space-y-1">
              {eyebrow ? (
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">{eyebrow}</p>
              ) : null}

              {(title || resolvedDescription) ? (
                <div className="space-y-1">
                  {title ? (
                    <h1 className={clsx('font-bold text-gray-900', compact ? 'text-2xl' : 'text-3xl')}>
                      {title}
                    </h1>
                  ) : null}

                  {resolvedDescription ? (
                    <p className={clsx('text-gray-600', compact ? 'text-sm' : 'text-base')}>
                      {resolvedDescription}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {(status || metadata) ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {status ? <div>{status}</div> : null}
                  {metadata ? <div className="text-sm text-gray-500">{metadata}</div> : null}
                </div>
              ) : null}
            </div>

            {(resolvedBackAction || secondaryActions || primaryAction) ? (
              <div className="flex shrink-0 flex-wrap items-center justify-start gap-2 md:justify-end">
                {resolvedBackAction ? <div>{resolvedBackAction}</div> : null}
                {secondaryActions ? <div>{secondaryActions}</div> : null}
                {primaryAction ? <div>{primaryAction}</div> : null}
              </div>
            ) : null}
          </div>

          {tabs ? <div className={compact ? 'mt-3' : 'mt-5'}>{tabs}</div> : null}
        </>
      ) : children}
    </div>
  );
};

const PageHeaderContent = ({
  children,
  className,
  ...props
}) => {
  const contentClasses = clsx(
    'flex items-center justify-between',
    className
  );

  return (
    <div className={contentClasses} {...props} data-otg-component={props['data-otg-component'] || 'PageHeaderContent'}>
      {children}
    </div>
  );
};

const PageHeaderTitle = ({
  children,
  subtitle,
  className,
  ...props
}) => {
  const titleClasses = clsx(
    'flex-1 min-w-0',
    className
  );

  return (
    <div className={titleClasses} {...props} data-otg-component={props['data-otg-component'] || 'PageHeaderTitle'}>
      <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl">
        {children}
      </h1>
      {subtitle && (
        <p className="mt-1 text-sm text-gray-500">
          {subtitle}
        </p>
      )}
    </div>
  );
};

const PageHeaderActions = ({
  children,
  className,
  ...props
}) => {
  const actionsClasses = clsx(
    'mt-4 flex space-x-3 md:mt-0 md:ml-4',
    className
  );

  return (
    <div className={actionsClasses} {...props} data-otg-component={props['data-otg-component'] || 'PageHeaderActions'}>
      {children}
    </div>
  );
};

const PageHeaderBreadcrumbs = ({
  items = [],
  className,
  ...props
}) => {
  if (!items.length) return null;

  const breadcrumbClasses = clsx(
    'mb-4',
    className
  );

  return (
    <nav className={breadcrumbClasses} {...props} data-otg-component={props['data-otg-component'] || 'PageHeaderBreadcrumbs'}>
      <ol className="flex items-center space-x-2 text-sm">
        {items.map((item, index) => (
          <li key={index} className="flex items-center">
            {index > 0 && (
              <svg
                className="w-4 h-4 text-gray-400 mx-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            )}
            {item.href ? (
              <a
                href={item.href}
                className="text-gray-500 hover:text-gray-700 font-medium"
              >
                {item.label}
              </a>
            ) : (
              <span
                className={clsx(
                  'font-medium',
                  index === items.length - 1
                    ? 'text-gray-900'
                    : 'text-gray-500'
                )}
              >
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

const PageHeaderTabs = ({
  tabs = [],
  activeTab,
  onChange,
  className,
  ...props
}) => {
  if (!tabs.length) return null;

  const tabsClasses = clsx(
    'mt-6 border-b border-gray-200',
    className
  );

  return (
    <div className={tabsClasses} {...props} data-otg-component={props['data-otg-component'] || 'PageHeaderTabs'}>
      <nav className="-mb-px flex space-x-8">
        {tabs.map((tab, index) => (
          <button
            key={tab.id || index}
            onClick={() => onChange?.(tab)}
            className={clsx(
              'py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap',
              activeTab === tab.id || activeTab === index
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            {tab.icon && (
              <span className="mr-2 inline-flex">
                {tab.icon}
              </span>
            )}
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-gray-500 bg-gray-100 rounded-full">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </nav>
    </div>
  );
};

const PageHeaderStats = ({
  stats = [],
  className,
  ...props
}) => {
  if (!stats.length) return null;

  const statsClasses = clsx(
    'mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3',
    className
  );

  return (
    <div className={statsClasses} {...props} data-otg-component={props['data-otg-component'] || 'PageHeaderStats'}>
      {stats.map((stat, index) => (
        <div
          key={stat.id || index}
          className="bg-white overflow-hidden shadow rounded-lg"
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {stat.icon && (
                  <div className="w-8 h-8 text-gray-500">
                    {stat.icon}
                  </div>
                )}
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {stat.label}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stat.value}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

PageHeader.Content = PageHeaderContent;
PageHeader.Title = PageHeaderTitle;
PageHeader.Actions = PageHeaderActions;
PageHeader.Breadcrumbs = PageHeaderBreadcrumbs;
PageHeader.Tabs = PageHeaderTabs;
PageHeader.Stats = PageHeaderStats;

export default PageHeader;
